import { useHttp } from "../http/useHttp";
import {
  DATE_FORMAT_REPORT_FILENAME_DEFAULT,
  dayjs,
  makeFilename,
} from "@synota-io/synota-shared-ui";
import { useAxiosQuery, UseAxiosQueryOptions } from "../http/useAxiosQuery";
import { paths } from "../generated-types";

type PaymentsReportOperation =
  paths["/dashboard/contracts/{contract_shared_uuid}/payments/reports/{format}"]["post"];
type PaymentsReportRequestBody =
  PaymentsReportOperation["requestBody"]["content"]["application/json"];
type PaymentsReportPath = PaymentsReportOperation["parameters"]["path"];

export type UsePaymentsReportInput = PaymentsReportRequestBody & {
  contract_uuid: PaymentsReportPath["contract_shared_uuid"];
};
export interface UsePaymentsReportMeta {
  contract_name: string;
}

export const usePaymentsReport = (
  input: UsePaymentsReportInput,
  meta: UsePaymentsReportMeta = { contract_name: "Contract" },
  options: Omit<UseAxiosQueryOptions<Blob>, "queryKey" | "queryFn"> = {},
) => {
  const { client } = useHttp();

  const {
    data,
    error,
    isFetching: isPending,
    status,
    refetch,
  } = useAxiosQuery<Blob>({
    queryKey: ["contract-payments-report-csv", input],
    queryFn: () =>
      client.post(`/contracts/${input.contract_uuid}/payments/reports/csv`, input, {
        responseType: "blob",
      }),
    ...options,
  });

  const isAvailable = status === "success" && !!data.data && data.data.size > 0;

  const download = () => {
    if (data && data.data.size > 0) {
      const blob = data.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        makeFilename(
          meta.contract_name,
          "Payments",
          input.energy_date_start
            ? dayjs(input.energy_date_start).format(DATE_FORMAT_REPORT_FILENAME_DEFAULT)
            : undefined,
          input.energy_date_end
            ? dayjs(input.energy_date_end).format(DATE_FORMAT_REPORT_FILENAME_DEFAULT)
            : undefined,
        )("csv"),
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } else {
      refetch().then((res) => {
        if (res.data?.data?.size && res.data.data.size > 0) {
          const blob = res.data.data;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            makeFilename(
              meta.contract_name,
              "Payments",
              input.energy_date_start
                ? dayjs(input.energy_date_start).format(DATE_FORMAT_REPORT_FILENAME_DEFAULT)
                : undefined,
              input.energy_date_end
                ? dayjs(input.energy_date_end).format(DATE_FORMAT_REPORT_FILENAME_DEFAULT)
                : undefined,
            )("csv"),
          );
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        }
      });
    }
  };

  return {
    download,
    error,
    isPending,
    status,
    isAvailable,
  };
};

export type UsePaymentsReportReturn = ReturnType<typeof usePaymentsReport>;
